import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dbad5ie9if4sq287d0sg";

type Status = "idle" | "submitting" | "success" | "error";

const feedbackTypes = ["功能建议", "内容纠错", "合作咨询", "其他问题"];

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [count, setCount] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setCount(0);
      return;
    }
    payload.delete("company_alt");

    setStatus("submitting");
    setFormError("");
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setCount(0);
      } else {
        setStatus("error");
        setFormError(typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交没有成功。请检查网络后重试。");
    }
  };

  return (
    <section id="feedback" className="scroll-mt-6 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
      <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
        <i className="ri-chat-smile-2-line text-accent-500" />
        用户反馈
      </h2>
      <p className="mt-1 text-[12px] text-foreground-600">
        想看的作品没找到？页面有问题？告诉我们，我们会认真看每一条留言。
      </p>

      {status === "success" ? (
        <div className="mt-4 rounded-md border border-primary-200 bg-primary-50 px-4 py-4">
          <p className="flex items-center gap-1.5 text-[13px] font-medium text-primary-800">
            <i className="ri-checkbox-circle-line" />
            反馈已提交，感谢你的建议！
          </p>
          <p className="mt-1 text-[12px] text-foreground-600">我们会尽快查看，并在后续版本中改进相关内容。</p>
          <button
            type="button"
            onClick={() => setStatus("idle")}
            className="mt-3 h-8 px-3 rounded-md border border-primary-300 text-[12px] text-primary-800 hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            再提交一条
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} data-readdy-form id="anime-feedback-form" className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rtl-field-hidden" aria-hidden="true">
            <label htmlFor="company_alt">公司名称（请勿填写）</label>
            <input
              id="company_alt"
              name="company_alt"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
          </div>

          <div>
            <label htmlFor="nickname" className="block text-[12px] text-foreground-700 mb-1.5">
              你的称呼
            </label>
            <input
              id="nickname"
              name="nickname"
              type="text"
              required
              maxLength={40}
              placeholder="例如：小动画迷"
              className="w-full h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-[12px] text-foreground-700 mb-1.5">
              联系邮箱
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              maxLength={100}
              placeholder="name@example.com"
              className="w-full h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
            />
          </div>

          <div className="sm:col-span-2">
            <span className="block text-[12px] text-foreground-700 mb-1.5">反馈类型</span>
            <div className="flex flex-wrap gap-1.5">
              {feedbackTypes.map((type, index) => (
                <label
                  key={type}
                  className="flex items-center gap-1.5 px-3 h-8 rounded-full border border-background-300 bg-background-50 text-[12px] text-foreground-700 cursor-pointer has-[:checked]:border-primary-400 has-[:checked]:bg-primary-50 has-[:checked]:text-primary-800"
                >
                  <input
                    type="radio"
                    name="feedback_type"
                    value={type}
                    defaultChecked={index === 0}
                    className="accent-primary-500"
                  />
                  {type}
                </label>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="anime_title" className="block text-[12px] text-foreground-700 mb-1.5">
              相关作品（选填）
            </label>
            <input
              id="anime_title"
              name="anime_title"
              type="text"
              maxLength={60}
              placeholder="例如：星海航线"
              className="w-full h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="message" className="block text-[12px] text-foreground-700 mb-1.5">
              反馈内容 <span className="text-foreground-400">（最多 500 字）</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              maxLength={500}
              rows={4}
              onChange={(event) => setCount(event.target.value.length)}
              placeholder="请描述你遇到的问题或建议…"
              className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 resize-none"
            />
            <p className="mt-1 text-right text-[11px] text-foreground-400">{count} / 500</p>
          </div>

          {status === "error" && formError ? (
            <div className="sm:col-span-2 rounded-md border border-accent-300 bg-accent-50 px-3 py-2 text-[12px] text-accent-900 flex items-start gap-1.5">
              <i className="ri-error-warning-line mt-0.5" />
              <span>{formError}</span>
            </div>
          ) : null}

          <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 pt-1">
            <p className="text-[11px] text-foreground-500">提交后会直接进入我们的反馈记录，不会公开你的邮箱。</p>
            <button
              type="submit"
              disabled={status === "submitting"}
              className="h-10 px-5 rounded-md bg-primary-500 text-background-50 text-[13px] font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {status === "submitting" ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  提交中…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-2-line" />
                  提交反馈
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}